import React from "react";
import { Qualification } from "../../domain/Qualification";
import { SectionProps } from "./SectionProps";
import { Grid } from "@material-ui/core";
import { Controller, useWatch } from "react-hook-form";
import { DatePicker } from "@ofqual/portal-components/components/DatePicker";
import { fieldLabels } from "./fieldLabels";
import { Error } from "../common/Error";
import { isFutureDate } from "@ofqual/portal-core/core/utils/dateUtils";

type DateDetailsProps = SectionProps<Qualification> & {
  shouldValidateOpStart: boolean;
};

export const DateDetails: React.FC<DateDetailsProps> = ({
  control,
  errors,
  disabled,
  user: { userType, token },
  lookupTracker,
  shouldValidateOpStart,
  setValue,
}) => {
  const {
    operationalStartDate,
    operationalEndDate,
    qualificationReviewDate,
    certificationEndDate,
  } = useWatch({
    control,
    name: [
      "operationalStartDate",
      "operationalEndDate",
      "qualificationReviewDate",
      "certificationEndDate",
    ],
  });

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} sm={6}>
        <Controller
          control={control}
          rules={{
            required: true,
            validate: (value) =>
              shouldValidateOpStart
                ? isFutureDate(value)
                  ? true
                  : false
                : true,
          }}
          name="operationalStartDate"
          as={
            <DatePicker
              returnDayBoundary="endOfDay"
              label={fieldLabels.operationalStartDate}
              readOnly={disabled}
              dataTest="operational-start-date"
              required
            />
          }
        />
        {errors.operationalStartDate?.type === "required" && (
          <Error data-test="op-start-date-validation">
            Operational start date is required
          </Error>
        )}
        {errors.operationalStartDate?.type === "validate" && (
          <Error>
            Operational start date must be after or the same as today's date
          </Error>
        )}
      </Grid>
      <Grid item xs={12} sm={6}>
        <Controller
          control={control}
          rules={{
            validate: {
              endCheckStartDate: (value) =>
                value
                  ? new Date(new Date(operationalStartDate).toDateString()) <=
                    new Date(new Date(value).toDateString())
                  : true,
              endCheckEndDateExists: (value) =>
                certificationEndDate ? value !== null : true,
              endCheckReviewDate: (value) => {
                return value && qualificationReviewDate !== null
                  ? new Date(
                      new Date(qualificationReviewDate).toDateString()
                    ) <= new Date(new Date(value).toDateString())
                  : true;
              },
            },
          }}
          name="operationalEndDate"
          as={
            <DatePicker
              returnDayBoundary="endOfDay"
              label={fieldLabels.operationalEndDate}
              readOnly={disabled}
              dataTest="operational-end-date"
              clearable={true}
            />
          }
        />
        {errors.operationalEndDate?.type === "endCheckStartDate" && (
          <Error>
            Qualification review date must be after or the same as operational
            start date
          </Error>
        )}
        {errors.operationalEndDate?.type === "endCheckEndDateExists" && (
          <Error>
            Operational end date must be present if certification end date is
            present
          </Error>
        )}
        {errors.operationalEndDate?.type === "endCheckReviewDate" && (
          <Error>
            Operational end date must be after or the same as qualification
            review date
          </Error>
        )}
      </Grid>
      <Grid item xs={12} sm={6}>
        <Controller
          control={control}
          name="qualificationReviewDate"
          rules={{
            validate: (value) =>
              value
                ? new Date(new Date(operationalStartDate).toDateString()) <=
                  new Date(new Date(value).toDateString())
                : true,
          }}
          as={
            <DatePicker
              returnDayBoundary="endOfDay"
              label={fieldLabels.qualificationReviewDate}
              readOnly={disabled}
              dataTest="qualification-review-date"
              clearable={true}
            />
          }
        />
        {errors.qualificationReviewDate?.type === "validate" && (
          <Error>
            Qualification review date must be after or the same as operational
            start date
          </Error>
        )}
      </Grid>
      <Grid item xs={12} sm={6}>
        <Controller
          control={control}
          name="certificationEndDate"
          rules={{
            validate: {
              checkStartDate: (value) =>
                value
                  ? new Date(new Date(operationalStartDate).toDateString()) <=
                    new Date(new Date(value).toDateString())
                  : true,
              checkReviewDate: (value) => {
                return value && qualificationReviewDate !== null
                  ? new Date(
                      new Date(qualificationReviewDate).toDateString()
                    ) <= new Date(new Date(value).toDateString())
                  : true;
              },
              checkOperationalDate: (value) =>
                value
                  ? new Date(new Date(operationalEndDate).toDateString()) <=
                    new Date(new Date(value).toDateString())
                  : true,
            },
          }}
          as={
            <DatePicker
              returnDayBoundary="endOfDay"
              label={fieldLabels.certificationEndDate}
              readOnly={disabled}
              dataTest="certification-end-date"
              clearable={true}
            />
          }
        />
        {errors.certificationEndDate?.type === "checkStartDate" && (
          <Error>
            Certification end date must be after or the same as qualification
            start date
          </Error>
        )}
        {errors.certificationEndDate?.type === "checkReviewDate" && (
          <Error>
            Certification end date must be after or the same as qualification
            review date
          </Error>
        )}
        {errors.certificationEndDate?.type === "checkOperationalDate" && (
          <Error>
            Certification end date must be after or the same as operational end
            date
          </Error>
        )}
      </Grid>
    </Grid>
  );
};
